import { NgClass } from '@angular/common';
import { Component, ElementRef, Renderer2, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { TranslocoDirective } from '@jsverse/transloco';
import { FidelityAvailablePages } from 'app/shared/components/fidelity-base-page/fidelity-base-page.types';
import { catchError, Observable } from 'rxjs';
import { fuseAnimations } from '../../../../../@fuse/animations';
import { BrandsService } from '../../../../core/brands/brands.service';
import { AppConfigService } from '../../../../core/configuration/configuration.service';
import { FidelityBasePageComponent } from '../../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { FidelityOverlayPageWrapperComponent } from '../../../../shared/components/fidelity-overlay-page-wrapper/fidelity-overlay-page-wrapper.component';
import { RegistrationFormComponent } from '../../../../shared/components/registration-form/registration-form.component';
import { ApiResult } from '../../../../shared/types/api-result.types';

@Component({
    selector: 'register-page',
    imports: [
        ReactiveFormsModule,
        TranslocoDirective,
        FidelityOverlayPageWrapperComponent,
        MatAutocompleteModule,
        RegistrationFormComponent,
        MatButton,
        NgClass,
    ],
    templateUrl: './register-page.component.html',
    styleUrl: './register-page.component.scss',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class RegisterPageComponent extends FidelityBasePageComponent {
    availablePages: FidelityAvailablePages = {};

    brandLogo = viewChild<ElementRef>('brandLogo');

    registrationModeChosen = signal<boolean>(false);
    hideCardInput = signal<boolean>(true);

    constructor(
        private _renderer2: Renderer2,
        private configService: AppConfigService,
        protected brandsService: BrandsService
    ) {
        super();

        // If the configuration does not enable registration from card, bypass the registration mode
        if (!this.configService.systemConfig.enableRegisterFromCard) {
            this.setNewRegistrationMode();
        }
    }

    onRegister(registerAction: Observable<ApiResult<string>>) {
        registerAction
            .pipe(takeUntilDestroyed(this.destroyRef), catchError(this.handlePageError.bind(this)))
            .subscribe((_) => {
                this.notifyResult(this.translocoService.translate('fidelity-app.register.notification-caption'));
                this.fidelityAppNavService.closePageAndClearHistory();
            });
    }

    setCardRegistrationMode() {
        this.fixLogoSize();

        this.registrationModeChosen.set(true);
        this.hideCardInput.set(false);
    }

    setNewRegistrationMode() {
        this.fixLogoSize();

        this.registrationModeChosen.set(true);
        this.hideCardInput.set(true);
    }

    private fixLogoSize() {
        // In the pre-registration question phase the logo can be resized,
        // but when moving to the actual registration form the resizing is disabled.
        // In order to make everything tidier, fix the current size of the logo.

        // If the brand logo viewChild is available, it means we are transitioning
        // from the pre-registration phase to the actual registration, fix the current size of the logo
        if (this.brandLogo()) {
            const width = this.brandLogo().nativeElement.width;
            const height = this.brandLogo().nativeElement.height;
            this._renderer2.setStyle(this.brandLogo().nativeElement, 'max-width', `${width}px`);
            this._renderer2.setStyle(this.brandLogo().nativeElement, 'max-height', `${height}px`);
        }
    }
}
